import classNames from "classnames";
import { FieldMessage } from "components/layout/forms/FormField";
import { makeStyles } from "components/providers/makeStyles";
import { T } from "components/util/t";
import get from "lodash/get";
import set from "lodash/set";
import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { uuid } from "utils/uuid";
import { CreatableSearchSelect } from "components/layout/forms/fields/Autocomplete/CreatableSearchSelect";
import { useCallbackRef } from "hooks/useCallbackRef";
import { useLocalizationContext } from "components/providers/LocalizationProvider";
import { useFormState } from "react-hook-form";

const useStyles = makeStyles((theme) => ({
    button: {
        "&.error": {
            borderColor: theme.palette.input.error,
        },
    },
    addDivision: {
        "&&": {
            width: "100%",
        },
    },
}));

interface AddDivisionProps {
    organisationDivisions: any[];
    divisions: any[];
    divisionNameField: string;
    divisionUuidField: string;
    divisionIdField: string;
    control: any;
    newDivisionMapper?: (division: any) => any;
    divisionsErrorPath?: string;
    array: {
        append: (division: any) => void;
        remove: (index: number) => void;
    };
    fieldName: string;
}

export const AddDivision: React.FC<AddDivisionProps> = ({
    organisationDivisions,
    divisions = [],
    divisionNameField,
    divisionUuidField,
    divisionIdField,
    control,
    newDivisionMapper = (v) => v,
    array,
    fieldName,
    divisionsErrorPath,
}) => {
    const [inputRef, node] = useCallbackRef();
    const [ref, nodeRef] = useCallbackRef();
    const classes = useStyles();
    const { errors, isDirty } = useFormState({ control });
    const errorPath = divisionsErrorPath
            ? get(errors, divisionsErrorPath.split("."))
            : errors[fieldName],
        error = errorPath?.root;
    const [addingDivision, setAddingDivision] = useState(false);
    const { translate } = useLocalizationContext();

    const field = {
        placeholder: "Type or select",
        options: organisationDivisions.reduce((options, division) => {
            const value = get(division, divisionIdField.split("."));
            if (
                divisions.find(
                    (d) => get(d, divisionIdField.split(".")) === value,
                )
            )
                return options;
            return [
                ...options,
                { label: get(division, divisionNameField.split(".")), value },
            ];
        }, []),
        inputRef,
        isValidNewOption: (inputValue: string) =>
            !divisions.find(
                (d) =>
                    get(d, divisionNameField.split("."))
                        .trim()
                        .toLowerCase() === inputValue.trim().toLowerCase(),
            ),
        createLabel: (label: string) =>
            translate?.("division_add_division", { division: label }),
    };

    const input = {
        value: null,
        onChange: (v: { value: any }) => {
            const value = v?.value;
            setAddingDivision(false);
            array.append(
                newDivisionMapper(
                    organisationDivisions.find(
                        (division) =>
                            get(division, divisionIdField.split(".")) === value,
                    ) ||
                        set(
                            set({}, divisionNameField.split("."), value),
                            divisionUuidField.split("."),
                            uuid(),
                        ),
                ),
            );
        },
    };

    useEffect(() => {
        if (addingDivision) {
            node?.focus();
        } else {
            if (error) nodeRef?.focus();
        }
    }, [node, nodeRef, error]);

    const addDivision = () => setAddingDivision(true);

    return (
        <tr>
            <td>
                <Box marginBottom={2}>
                    {addingDivision ? (
                        <CreatableSearchSelect
                            field={field}
                            input={input}
                            hasError={!!error}
                        />
                    ) : (
                        <>
                            <Button
                                className={classNames(
                                    classes.button,
                                    classes.addDivision,
                                    { error: !!error },
                                )}
                                variant="outlined"
                                onClick={addDivision}
                                ref={ref}
                            >
                                <T>Add division</T>
                            </Button>
                        </>
                    )}
                    <FieldMessage
                        isDirty={isDirty}
                        errorMessage={error?.message}
                    />
                </Box>
            </td>
        </tr>
    );
};
